import {
  CurrentState,
  GetSessionTokenRequest,
  LoginRequest,
  OnStateChangedCallback,
  SessionManagerInterface,
  StateType,
  TokenLoginRequest,
} from '@central-products/interface-session-manager';
import { createCasClient, CasClient } from '@central-products/cas-client';
import { errors } from './errors';
import { WebLoginPreferencesInterface } from '@central-products/interface-web-login-preferences';
import { CasError, resolveLoginError } from './utils';
import { SessionMarkActiveService } from './sessionMarkActiveService';
import { SessionPassiveLoginListener } from './SessionPassiveLoginListener';
import { SessionLogoutListener, SessionLogoutListenerMock } from './sessionLogoutListener';
import {
  CasSessionManagerConfig,
  CasSessionManagerDependencies,
  SessionMarkActiveServiceInterface,
  SessionPassiveLoginListenerInterface,
  SessionLogoutListenerInterface,
  Jurisdiction,
  SessionMarkActiveServiceSessionExpiration,
} from './types';
import { SESSION_EXPIRATION_INITIAL_STATE } from './constants';
export class CasSessionManager extends SessionManagerInterface {
  public static SESSION_IDENTIFIER = 'CSM-ID';
  private readonly casClient: CasClient;
  private readonly webLoginPreferences: WebLoginPreferencesInterface;
  private readonly sessionMarkActiveService: SessionMarkActiveServiceInterface;
  private readonly sessionPassiveLoginListener: SessionPassiveLoginListenerInterface;
  private readonly sessionLogoutListener: SessionLogoutListenerInterface;
  private enableSessionRenew: boolean = false;
  private state: StateType = StateType.UNKNOWN;
  private sessionExpiration: SessionMarkActiveServiceSessionExpiration = SESSION_EXPIRATION_INITIAL_STATE;
  private onStateChangedCallback?: OnStateChangedCallback;

  constructor(
    {
      jurisdiction,
      casUrl,
      serviceUrl,
      sessionMarkActive: sessionMarkActiveConfig = {},
      sessionPassiveLoginListener: sessionPassiveLoginListenerConfig,
      sessionLogoutListener: sessionLogoutListenerConfig,
      enableFlagHandling = false,
      enableRecaptcha = false,
      enableSessionRenew = false,
    }: CasSessionManagerConfig,
    dependencies: CasSessionManagerDependencies
  ) {
    super();

    const validJurisdiction =
      typeof jurisdiction !== 'undefined'
        ? jurisdiction
        : (casUrl
            .match(/^(?:\/\/|[^\/]+)*/)
            ?.join('')
            .match(/\.\w{2,3}\b/g)
            ?.join('')
            .replace(/^\./, '') as Jurisdiction);
    this.webLoginPreferences = dependencies['web-login-preferences'];
    this.enableSessionRenew = enableSessionRenew;
    this.casClient = createCasClient({
      casUrl,
      serviceUrl: sessionLogoutListenerConfig ? `${sessionLogoutListenerConfig.url}/logout` : (serviceUrl as string),
      enableFlagHandling,
      enableRecaptcha,
    });
    this.sessionMarkActiveService = new SessionMarkActiveService({
      ...sessionMarkActiveConfig,
      casClient: this.casClient,
      onErrorCallback: () => {
        this.sessionLogoutListener.close();
        this.setNotLoggedIn();
        this.sessionPassiveLoginListener.init();
      },
      onImAliveCallback: ({ timeToKill, expiresAt }) => {
        this.setState({ state: this.state, timeToKill, expiresAt });
      },
    });
    /**
     * TODO: proper passive login support via session listener
     * For nor this feature is only limited to native apps in order to
     * avoid overwhelming CAS with high volume of requests.
     */
    this.sessionPassiveLoginListener = new SessionPassiveLoginListener({
      ...sessionPassiveLoginListenerConfig,
      casClient: this.casClient,
      onLoginCallback: () => {
        this.clearSessionIdentifier();
        this.setState({ state: StateType.LOGGED_IN, ...SESSION_EXPIRATION_INITIAL_STATE });
        this.sessionLogoutListener.init();
      },
    });
    this.sessionLogoutListener = sessionLogoutListenerConfig
      ? new SessionLogoutListener({
          ...sessionLogoutListenerConfig,
          jurisdiction: validJurisdiction,
          casClient: this.casClient,
          onLogoutCallback: () => {
            this.sessionMarkActiveService.dispose();
            this.setNotLoggedIn();
            this.sessionPassiveLoginListener.init();
          },
        })
      : new SessionLogoutListenerMock();
  }

  public async init() {
    if (this.enableSessionRenew === true) {
      this.setNotLoggedIn();
    } else {
      try {
        const { imAlive, timeToKill, expiresAt } = await this.sessionMarkActiveService.imAlive();

        if (imAlive) {
          this.setState({ state: StateType.LOGGED_IN, timeToKill, expiresAt });
          this.sessionLogoutListener.init();
        } else {
          this.setNotLoggedIn();
          /**
           * TODO: refactor SessionMarkActiveService (use retryable function, rethink error callback)
           * SessionPassiveLoginListener `init` is called by SessionMarkActiveService callback function
           * named `onErrorCallback` after CAS `imAlive` returns 401, until that part of the code is
           * changed to behave differently SessionPassiveLoginListener `init` cannot be called from here.
           */
        }
      } catch (error) {
        this.sessionMarkActiveService.dispose();
        this.sessionLogoutListener.close();
        this.setNotLoggedIn();
        this.sessionPassiveLoginListener.init();
      }
    }
  }

  public async getState() {
    return { state: this.state, ...this.sessionExpiration };
  }

  public onStateChanged(callback: OnStateChangedCallback) {
    this.onStateChangedCallback = callback;
    // tslint:disable-next-line
    return () => {};
  }

  public async getSessionToken({ serviceUrl }: GetSessionTokenRequest) {
    if (this.state !== StateType.LOGGED_IN) {
      throw {
        state: this.state,
        error: errors.CasSessionManager.getSessionToken.stateInvalid,
      };
    }

    try {
      const { next, serviceTicket } = await this.casClient.init({}, serviceUrl);

      return {
        sessionToken: serviceTicket,
        sessionSyncUrl: next,
      };
    } catch (error) {
      throw {
        state: this.state,
        error: error
          ? errors.CasSessionManager.getSessionToken.operationFailed
          : errors.CasSessionManager.getSessionToken.stateInvalid,
      };
    }
  }

  public async getSessionIdentifier() {
    return { sessionId: window.localStorage?.getItem?.(CasSessionManager.SESSION_IDENTIFIER) ?? null };
  }

  public async logIn({ username, password, token }: LoginRequest) {
    if (this.state === StateType.LOGGED_IN) {
      throw {
        state: this.state,
        error: errors.CasSessionManager.logIn.stateInvalid,
      };
    }

    if (!username || !password) {
      throw {
        state: this.state,
        error: errors.CasSessionManager.logIn.apiCallInvalid,
      };
    }

    try {
      const preferences = await this.webLoginPreferences.get();
      const loginParams = {
        username,
        password,
        rememberMe: preferences.keepLoggedIn,
        rememberUsername: !!preferences.rememberUsername,
        token,
        renew: this.enableSessionRenew,
      };

      const { tempPassword, serviceTicket, next } = await this.casClient.login(loginParams);

      this.sessionPassiveLoginListener.close();
      this.clearSessionIdentifier();
      this.setState({ state: StateType.LOGGED_IN, ...SESSION_EXPIRATION_INITIAL_STATE });
      this.sessionLogoutListener.init(serviceTicket);

      return {
        state: this.state,
        ...(tempPassword ? { tempPassword, tempPasswordUrl: next } : {}),
      };
    } catch (error) {
      this.setNotLoggedIn();

      throw {
        state: this.state,
        error: resolveLoginError(error as CasError),
      };
    }
  }

  public async tokenLogIn({ token }: TokenLoginRequest) {
    if (this.state === StateType.LOGGED_IN) {
      throw {
        state: this.state,
        error: errors.CasSessionManager.logIn.stateInvalid,
      };
    }

    if (!token) {
      throw {
        state: this.state,
        error: errors.CasSessionManager.logIn.apiCallInvalid,
      };
    }

    try {
      await this.casClient.tokenLogin({ token, renew: this.enableSessionRenew });

      let ticketNumber = '';

      if (!(this.sessionLogoutListener instanceof SessionLogoutListenerMock)) {
        /**
         * TODO: ask BE if tokenLogin can return service ticket (we would need to pass service url)
         */
        const response = await this.casClient.init({});
        ticketNumber = response.serviceTicket;
      }

      this.sessionPassiveLoginListener.close();
      this.clearSessionIdentifier();
      this.setState({ state: StateType.LOGGED_IN, ...SESSION_EXPIRATION_INITIAL_STATE });
      this.sessionLogoutListener.init(ticketNumber);

      return { state: this.state };
    } catch (error) {
      this.setNotLoggedIn();

      throw {
        state: this.state,
        error: resolveLoginError(error as CasError),
      };
    }
  }

  public async logOut() {
    if (this.state !== StateType.LOGGED_IN) {
      throw {
        state: this.state,
        error: errors.CasSessionManager.logOut.stateInvalid,
      };
    }

    try {
      await this.casClient.logout();
      this.sessionMarkActiveService.dispose();
      this.sessionLogoutListener.close();
      this.setNotLoggedIn();
      this.sessionPassiveLoginListener.init();

      return { state: this.state };
    } catch (error) {
      throw { state: this.state, error: errors.CasSessionManager.logOut.operationFailed };
    }
  }

  public async markActive() {
    if (this.state !== StateType.LOGGED_IN) {
      return;
    }

    this.sessionMarkActiveService.markActive();
  }

  private setNotLoggedIn = () => this.setState({ state: StateType.NOT_LOGGED_IN, ...SESSION_EXPIRATION_INITIAL_STATE });

  private setState = (sessionInfo: CurrentState) => {
    const { state, timeToKill, expiresAt } = sessionInfo;
    if (
      this.state === state &&
      this.sessionExpiration.timeToKill === timeToKill &&
      this.sessionExpiration.expiresAt === expiresAt
    ) {
      return;
    }

    if (state === StateType.LOGGED_IN) {
      if (!this.hasSessionIdentifier()) {
        this.setSessionIdentifier();
      }
    } else if (state === StateType.NOT_LOGGED_IN) {
      this.clearSessionIdentifier();
    }

    if (state === StateType.LOGGED_IN) {
      this.sessionMarkActiveService.trackUserActivity();
    } else {
      this.sessionMarkActiveService.untrackUserActivity();
    }

    this.state = state;
    this.sessionExpiration = { timeToKill, expiresAt };

    if (this.onStateChangedCallback) {
      this.onStateChangedCallback({ state, expiresAt, timeToKill });
    }
  };

  private hasSessionIdentifier = () => {
    return window.localStorage?.getItem?.(CasSessionManager.SESSION_IDENTIFIER) != null;
  };

  private setSessionIdentifier = () => {
    window.localStorage?.setItem?.(CasSessionManager.SESSION_IDENTIFIER, `${Date.now()}`);
  };

  private clearSessionIdentifier = () => {
    window.localStorage?.removeItem?.(CasSessionManager.SESSION_IDENTIFIER);
  };
}
